"use client"

import {
    useQueryClient,
    type Query,
    type QueryClient,
} from "@tanstack/react-query"
import { usePubSub } from "./usePubSub"
import { createCoalescerRegistry } from "@/utils/coalesce.utils"
import { reportClientWarning } from "@/lib/clientReporting"
import {
    EXPLORER_COUNTS_REFRESH,
    isExplorerCountsQueryKey,
} from "@/constants/explorerCounts.constants"

export const DOCUMENT_SYNC_COALESCE_MS = 10_000
export const DOCUMENT_SYNC_JITTER_MS = 5_000

const broadInvalidators = createCoalescerRegistry<QueryClient>()

/**
 * workspaceDoc list procedures with no poll or window-focus refetch; only
 * this broadcast's active refetch clears their staleness.
 */
const BROADCAST_REFETCH_PROCEDURES: ReadonlySet<string> = new Set([
    "listExplorerView",
    "listExplorerSavedViews",
    "getExplorerHiddenViews",
    "listExplorerBatches",
    "listQuotesByVendor",
])

const isBroadcastRefetchQueryKey = (queryKey: readonly unknown[]): boolean => {
    const [path] = queryKey
    return (
        Array.isArray(path) &&
        path[0] === "workspaceDoc" &&
        typeof path[1] === "string" &&
        BROADCAST_REFETCH_PROCEDURES.has(path[1])
    )
}

const shouldStaleOnBroadcast = (query: Query): boolean =>
    EXPLORER_COUNTS_REFRESH === "broadcast" ||
    !isExplorerCountsQueryKey(query.queryKey)

const shouldRefetchOnBroadcast = (query: Query): boolean =>
    isBroadcastRefetchQueryKey(query.queryKey) && shouldStaleOnBroadcast(query)

const DOCUMENT_KEYED_QUERIES: ReadonlyArray<{
    path: readonly [string, string]
    input: (documentId: string) => Record<string, string>
}> = [
    {
        path: ["workspaceDoc", "getById"],
        input: documentId => ({ documentId }),
    },
    {
        path: ["workspaceDoc", "getOutboundActions"],
        input: documentId => ({ documentId }),
    },
    {
        path: ["workspaceDoc", "listQuotesByShipment"],
        input: documentId => ({ shipmentDocId: documentId }),
    },
]

function invalidateDocumentQueries({
    queryClient,
    documentId,
}: {
    queryClient: QueryClient
    documentId: string
}): void {
    for (const { path, input } of DOCUMENT_KEYED_QUERIES) {
        queryClient.invalidateQueries({
            queryKey: [path, { input: input(documentId) }],
            refetchType: "active",
        })
    }
}

function invalidateRelatedShipmentQuoteQueries({
    queryClient,
    shipmentIds,
}: {
    queryClient: QueryClient
    shipmentIds: readonly string[]
}): void {
    for (const shipmentDocId of shipmentIds) {
        queryClient.invalidateQueries({
            queryKey: [
                ["workspaceDoc", "listQuotesByShipment"],
                { input: { shipmentDocId } },
            ],
            refetchType: "active",
        })
    }
}

function markWorkspaceQueriesStale({
    queryClient,
}: {
    queryClient: QueryClient
}): void {
    queryClient.invalidateQueries({
        queryKey: [["workspaceDoc"]],
        refetchType: "none",
        predicate: shouldStaleOnBroadcast,
    })
}

export function useDocumentSync({
    coalesceMs = DOCUMENT_SYNC_COALESCE_MS,
    jitterMs = DOCUMENT_SYNC_JITTER_MS,
}: { coalesceMs?: number; jitterMs?: number } = {}) {
    const queryClient = useQueryClient()
    const broadInvalidator = broadInvalidators.forKey({
        key: queryClient,
        windowMs: coalesceMs,
        jitterMs,
        run: () => {
            try {
                queryClient.invalidateQueries({
                    queryKey: [["workspaceDoc"]],
                    refetchType: "active",
                    predicate: shouldRefetchOnBroadcast,
                })
            } catch (err) {
                reportClientWarning({
                    message: "useDocumentSync: invalidation failed",
                    cause: err as Error,
                    tags: { surface: "useDocumentSync" },
                })
            }
        },
    })

    usePubSub([
        {
            events: [
                "documentUpdated",
                "workspaceDocCreated",
                "shipmentDocCreated",
            ],
            callback: event => {
                try {
                    broadInvalidator.schedule()

                    if (
                        typeof event?.documentId === "string" &&
                        event.documentId.length > 0
                    ) {
                        invalidateDocumentQueries({
                            queryClient,
                            documentId: event.documentId,
                        })
                    }

                    if (
                        event?.type === "documentUpdated" &&
                        event.relatedShipmentIds &&
                        event.relatedShipmentIds.length > 0
                    ) {
                        invalidateRelatedShipmentQuoteQueries({
                            queryClient,
                            shipmentIds: event.relatedShipmentIds,
                        })
                    }

                    markWorkspaceQueriesStale({ queryClient })
                } catch (err) {
                    reportClientWarning({
                        message: "useDocumentSync: invalidation failed",
                        cause: err as Error,
                        tags: { surface: "useDocumentSync" },
                    })
                }
            },
        },
    ])
}
